import { ArrowLeft, CalendarX, Copy, Save, Send, Trash2, TriangleAlert, Users } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { Segmented } from '@/components/ui/segmented'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/ui/states'
import { Textarea } from '@/components/ui/textarea'
import { useMe } from '@/features/auth/api'
import { errorMessage, isApiError } from '@/lib/api/errors'
import { cn } from '@/lib/cn'
import { formatDate, formatDateTime, numberFmt } from '@/lib/format'
import { useNow } from '@/lib/use-now'
import {
  type Campaign,
  type CampaignInput,
  type CampaignKind,
  type CampaignResults,
  type GroupFilters,
  type MarketingList,
  useAudience,
  useCampaign,
  useDeleteCampaign,
  useMarketing,
  useMarketingPreview,
  useSaveCampaign,
  useScheduleCampaign,
  useSendCampaign,
  useSendTestMarketing,
  useUnscheduleCampaign,
} from './api'
import { FilterFields } from './filters'
import { chipFor, cleanFilters, describeFilters, dollars, NOT_TRACKED, percent } from './format'
import { GroupDialog } from './marketing-page'
import { MarketingSends } from './sends'

const blank: CampaignInput = {
  kind: 'marketing',
  name: '',
  subject: '',
  preheader: null,
  body: '',
  button_label: null,
  button_url: null,
  filters: {},
  group_id: null,
  skip_recent_days: 3,
}

/** Write, test and send a marketing email: /marketing/new or /marketing/:campaignId. */
export function CampaignEditorPage() {
  const { campaignId } = useParams()
  const campaign = useCampaign(campaignId)
  const list = useMarketing()

  if ((campaignId !== undefined && campaign.isPending) || list.isPending) return <Skeleton className="h-96" />
  if (campaign.isError) return <ErrorState error={campaign.error} onRetry={() => void campaign.refetch()} />
  if (list.isError) return <ErrorState error={list.error} onRetry={() => void list.refetch()} />

  return <Editor key={campaign.data?.id ?? 'new'} campaign={campaign.data ?? null} list={list.data} />
}

/** The fields the editor saves, from a campaign. */
function inputOf(c: Campaign): CampaignInput {
  return {
    kind: c.kind,
    name: c.name,
    subject: c.subject,
    preheader: c.preheader,
    body: c.body,
    button_label: c.button_label,
    button_url: c.button_url,
    filters: c.filters,
    group_id: c.group_id,
    skip_recent_days: c.skip_recent_days,
  }
}

function Editor({ campaign, list }: { campaign: Campaign | null; list: MarketingList }) {
  const navigate = useNavigate()
  const location = useLocation()
  const me = useMe()
  const meta = list.meta
  const save = useSaveCampaign(campaign?.id)
  const remove = useDeleteCampaign()
  const sendTest = useSendTestMarketing()
  const unschedule = useUnscheduleCampaign(campaign?.id ?? '')
  const body = useRef<HTMLTextAreaElement>(null)
  // A copy of another email (its Duplicate button) starts a new draft with its content and group.
  const copy = campaign === null ? (location.state as { copyOf?: CampaignInput } | null)?.copyOf : undefined
  const [values, setValues] = useState<CampaignInput>(
    campaign ? inputOf(campaign) : copy ? { ...copy, name: `${copy.name} (copy)` } : blank,
  )
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [tab, setTab] = useState<'write' | 'preview'>('write')
  const confirmSaved =
    campaign !== null &&
    (location.state as { confirmCampaign?: string } | null)?.confirmCampaign === campaign.id
  const [dialog, setDialog] = useState<'send' | 'delete' | 'group' | null>(confirmSaved ? 'send' : null)
  const role = me.data?.role
  const draft = campaign === null || campaign.status === 'draft'
  const canEdit = draft && (role === 'owner' || role === 'admin')
  const canSend = draft && role === 'owner'
  const canDelete = draft
    ? role === 'owner' || role === 'admin'
    : role === 'owner' && campaign.status !== 'sending'
  const scheduled = draft && Boolean(campaign?.scheduled_at)
  const preview = useMarketingPreview(values, tab === 'preview')
  // The group is counted as it's chosen; the count a draft confirms is counted again on sending.
  const service = values.kind === 'service'
  const [countFor, setCountFor] = useState({ filters: values.filters, skip: values.skip_recent_days })
  useEffect(() => {
    const t = setTimeout(() => setCountFor({ filters: values.filters, skip: values.skip_recent_days }), 300)
    return () => clearTimeout(t)
  }, [values.filters, values.skip_recent_days])
  const audience = useAudience(countFor.filters, countFor.skip, campaign?.id, values.kind)
  const set = <K extends keyof CampaignInput>(key: K, value: CampaignInput[K]) =>
    setValues((v) => ({ ...v, [key]: value }))
  // What's saved is what's sent: unsaved edits must be saved first.
  const dirty =
    campaign !== null &&
    JSON.stringify(inputOf(campaign)) !== JSON.stringify({ ...values, filters: cleanFilters(values.filters) })

  useEffect(() => {
    if (confirmSaved) {
      void navigate(location.pathname + location.search + location.hash, { replace: true, state: null })
    }
  }, [confirmSaved, location.pathname, location.search, location.hash, navigate])

  const submit = (confirmAfterSave = false) => {
    if (!canEdit || save.isPending || (confirmAfterSave && !canSend)) return
    setErrors({})
    save.mutate(
      {
        ...values,
        filters: cleanFilters(values.filters),
        preheader: values.preheader?.trim() || null,
        button_label: values.button_label?.trim() || null,
        button_url: values.button_url?.trim() || null,
      },
      {
        onSuccess: (saved) => {
          setValues(inputOf(saved))
          toast.success('Draft saved. Nothing was sent.')
          if (!campaign) {
            void navigate(`/marketing/${saved.id}`, {
              replace: true,
              state: confirmAfterSave ? { confirmCampaign: saved.id } : null,
            })
          } else if (confirmAfterSave) setDialog('send')
        },
        onError: (err) => {
          if (isApiError(err) && err.isValidation) {
            setErrors(Object.fromEntries(Object.entries(err.errors).map(([k, v]) => [k, v[0] ?? ''])))
            setTab('write')
          } else toast.error(errorMessage(err))
        },
      },
    )
  }

  const startSending = () => {
    if (!canSend || save.isPending) return
    if (campaign === null || dirty) submit(true)
    else setDialog('send')
  }

  const fromGroup = (id: string) => {
    const group = list.groups.find((g) => g.id === id)
    setValues((v) => ({ ...v, group_id: group?.id ?? null, filters: group ? group.filters : v.filters }))
  }

  return (
    <>
      <Link
        to="/marketing"
        className="mb-4 inline-flex items-center gap-1.5 text-[13px] text-ink-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" /> Marketing
      </Link>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div className="grid gap-1">
          <h1 className="display text-2xl text-ink">{campaign ? campaign.name : 'New marketing email'}</h1>
          {campaign && (
            <p className="flex flex-wrap items-center gap-2 text-[13px] text-ink-muted">
              <span className={cn('chip', chipFor(campaign).tone)}>{chipFor(campaign).label}</span>
              {campaign.sent_at
                ? `Sent ${formatDate(campaign.sent_at)}${campaign.sent_by ? ` by ${campaign.sent_by}` : ''} to ${numberFmt.format(campaign.recipients)} ${campaign.recipients === 1 ? 'person' : 'people'}`
                : scheduled
                  ? `Goes out ${formatDateTime(campaign.scheduled_at)}${campaign.scheduled_by ? ` · scheduled by ${campaign.scheduled_by}` : ''}`
                  : campaign.updated_at &&
                    `Updated ${formatDate(campaign.updated_at)}${campaign.updated_by ? ` by ${campaign.updated_by}` : ''}`}
            </p>
          )}
        </div>
        <div className="grid justify-items-end gap-2">
          <div className="flex flex-wrap gap-2">
            {draft && (
              <Button
                variant="secondary"
                onClick={() => submit()}
                loading={save.isPending}
                disabled={!canEdit || (campaign !== null && !dirty)}
              >
                <Save /> Save draft
              </Button>
            )}
            {draft && (
              <Button
                disabled={!canSend || save.isPending}
                title={role !== 'owner' ? 'Only owners can send marketing emails.' : undefined}
                aria-describedby="campaign-send-hint"
                onClick={startSending}
              >
                <Send /> Send…
              </Button>
            )}
            {scheduled && canSend && (
              <Button
                variant="secondary"
                loading={unschedule.isPending}
                onClick={() =>
                  unschedule.mutate(undefined, {
                    onSuccess: () => toast.success('Unscheduled. It’s a draft again; nothing was sent.'),
                    onError: (err) => toast.error(errorMessage(err)),
                  })
                }
              >
                <CalendarX /> Unschedule
              </Button>
            )}
            <Button
              variant="secondary"
              loading={sendTest.isPending}
              disabled={!values.subject.trim() || !values.body.trim()}
              onClick={() =>
                sendTest.mutate(values, {
                  onSuccess: (r) => toast.success(`Test sent to ${r.sent_to}.`),
                  onError: (err) => toast.error(errorMessage(err)),
                })
              }
            >
              Send me a test
            </Button>
            {campaign && (role === 'owner' || role === 'admin') && (
              <Button
                variant="ghost"
                onClick={() => void navigate('/marketing/new', { state: { copyOf: inputOf(campaign) } })}
              >
                <Copy /> Duplicate
              </Button>
            )}
            {campaign && canDelete && (
              <Button variant="ghost" disabled={save.isPending} onClick={() => setDialog('delete')}>
                <Trash2 /> Delete
              </Button>
            )}
          </div>
          <p id="campaign-send-hint" className="max-w-md text-right text-xs text-ink-subtle">
            {!draft
              ? campaign.status === 'sending'
                ? 'It’s going out now. Results fill in as people open and click it.'
                : 'Sent emails can’t be edited. Duplicate it to send a new version.'
              : role === 'owner'
                ? 'Send saves your draft first, then asks you to confirm how many people get it, now or at a time you choose.'
                : 'Owners send marketing emails.'}
          </p>
        </div>
      </div>

      {campaign?.results && <Results results={campaign.results} recipients={campaign.recipients} />}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <Card className="grid h-fit content-start gap-5 p-5">
          <Field
            label="Name"
            htmlFor="mkt-name"
            required
            error={errors.name}
            hint="For the team only; people never see it."
          >
            <Input
              id="mkt-name"
              disabled={!canEdit || save.isPending}
              value={values.name}
              onChange={(e) => set('name', e.target.value)}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Subject" htmlFor="mkt-subject" required error={errors.subject}>
              <Input
                id="mkt-subject"
                disabled={!canEdit || save.isPending}
                value={values.subject}
                onChange={(e) => set('subject', e.target.value)}
              />
            </Field>
            <Field
              label="Preview text"
              htmlFor="mkt-preheader"
              error={errors.preheader}
              hint="Shown after the subject in most inboxes."
            >
              <Input
                id="mkt-preheader"
                disabled={!canEdit || save.isPending}
                value={values.preheader ?? ''}
                onChange={(e) => set('preheader', e.target.value)}
              />
            </Field>
          </div>

          <div className="grid gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <label
                htmlFor="mkt-body"
                className="text-[13px] font-medium text-ink after:ml-0.5 after:text-danger after:content-['*']"
              >
                Message
              </label>
              <Segmented<'write' | 'preview'>
                label="Editor view"
                value={tab}
                onChange={setTab}
                options={[
                  { value: 'write', label: 'Write' },
                  { value: 'preview', label: 'Preview' },
                ]}
              />
            </div>
            {tab === 'write' ? (
              <>
                <Textarea
                  id="mkt-body"
                  ref={body}
                  disabled={!canEdit || save.isPending}
                  className="min-h-[22rem] font-mono text-[13px] leading-relaxed"
                  value={values.body}
                  onChange={(e) => set('body', e.target.value)}
                  aria-invalid={errors.body ? true : undefined}
                />
                {errors.body && (
                  <p role="alert" className="text-xs text-danger">
                    {errors.body}
                  </p>
                )}
                {canEdit && (
                  <div className="flex flex-wrap items-center gap-1.5 pt-1">
                    <span className="text-xs text-ink-subtle">Insert:</span>
                    {Object.entries(meta.merge_tags).map(([tag, label]) => (
                      <button
                        key={tag}
                        type="button"
                        disabled={save.isPending}
                        title={label}
                        onClick={() => set('body', values.body + `{{${tag}}}`)}
                        className="rounded-full border border-border-strong px-2 py-0.5 font-mono text-[11px] text-ink-muted hover:border-ink hover:text-ink"
                      >
                        {`{{${tag}}}`}
                      </button>
                    ))}
                  </div>
                )}
                <p className="text-xs text-ink-subtle">
                  Markdown: <code># Heading</code>, <code>**bold**</code>, <code>- list item</code>,{' '}
                  <code>[link](https://…)</code>. Put <code>{'{{button}}'}</code> on its own line to place the
                  button; otherwise it goes at the end. Every email ends with an unsubscribe link.
                </p>
              </>
            ) : !values.subject.trim() || !values.body.trim() ? (
              <div role="status" className="grid gap-3 rounded-md border border-border bg-surface-sunken p-5">
                <p className="text-sm text-ink-muted">Add a subject and message to see the email.</p>
                <Button variant="secondary" size="sm" className="w-fit" onClick={() => setTab('write')}>
                  Continue writing
                </Button>
              </div>
            ) : preview.isError ? (
              <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />
            ) : preview.isPending || preview.isFetching ? (
              <div role="status" className="grid gap-2">
                <p className="text-sm text-ink-muted">Loading preview…</p>
                <Skeleton className="h-[40rem] w-full rounded-md" />
              </div>
            ) : (
              <div className="grid gap-2">
                <p className="text-[13px] text-ink-muted">
                  Subject: <strong className="text-ink">{preview.data.subject}</strong>
                  <span className="block text-xs text-ink-subtle">With made-up details, and your name.</span>
                </p>
                <iframe
                  title="Email preview"
                  sandbox=""
                  srcDoc={preview.data.html}
                  className="h-[40rem] w-full rounded-md border border-border bg-white"
                />
              </div>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Button text"
              htmlFor="mkt-button-label"
              error={errors.button_label}
              hint="Optional."
            >
              <Input
                id="mkt-button-label"
                disabled={!canEdit || save.isPending}
                value={values.button_label ?? ''}
                onChange={(e) => set('button_label', e.target.value)}
              />
            </Field>
            <Field
              label="Button link"
              htmlFor="mkt-button-url"
              error={errors.button_url}
              hint="A page in the app (/…), {{workspace_url}}/…, or https://…"
            >
              <Input
                id="mkt-button-url"
                disabled={!canEdit || save.isPending}
                value={values.button_url ?? ''}
                onChange={(e) => set('button_url', e.target.value)}
              />
            </Field>
          </div>
        </Card>

        <div className="grid h-fit gap-4">
          <Card className="grid gap-4 p-5" aria-label="Who gets it">
            <div className="flex items-center gap-2">
              <Users className="size-4 text-ink-muted" aria-hidden="true" />
              <b className="text-ink">Who gets it</b>
            </div>
            <Field label="Kind of email" htmlFor="mkt-kind" hint={meta.kinds[values.kind].description}>
              <NativeSelect
                id="mkt-kind"
                disabled={!canEdit || save.isPending}
                value={values.kind}
                onChange={(e) => set('kind', e.target.value as CampaignKind)}
              >
                {(Object.keys(meta.kinds) as CampaignKind[]).map((k) => (
                  <option key={k} value={k}>
                    {meta.kinds[k].label}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <div
              role="status"
              aria-live="polite"
              className="grid gap-1 rounded-md bg-surface-sunken p-3 text-[13px]"
            >
              {audience.isPending ? (
                <span className="text-ink-muted">Counting…</span>
              ) : audience.isError ? (
                <span className="text-danger">{errorMessage(audience.error)}</span>
              ) : (
                <>
                  <b className="text-ink">
                    {numberFmt.format(audience.data.count)} {audience.data.count === 1 ? 'person' : 'people'}{' '}
                    now
                  </b>
                  <span className="text-ink-muted">{describeFilters(values.filters, meta, values.kind)}</span>
                  {audience.data.sample.length > 0 && (
                    <span className="text-xs text-ink-subtle" style={{ wordBreak: 'break-all' }}>
                      Such as {audience.data.sample.join(', ')}
                    </span>
                  )}
                </>
              )}
            </div>
            {list.groups.length > 0 && (
              <Field label="Start from a saved group" htmlFor="mkt-group">
                <NativeSelect
                  id="mkt-group"
                  disabled={!canEdit || save.isPending}
                  value={values.group_id ?? ''}
                  onChange={(e) => fromGroup(e.target.value)}
                >
                  <option value="">None</option>
                  {list.groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name} ({numberFmt.format(g.count)})
                    </option>
                  ))}
                </NativeSelect>
              </Field>
            )}
            <FilterFields
              filters={values.filters}
              meta={meta}
              disabled={!canEdit || save.isPending}
              onChange={(filters: GroupFilters) => setValues((v) => ({ ...v, filters, group_id: null }))}
            />
            {!service && (
              <Field
                label="Leave out people emailed in the last"
                htmlFor="mkt-skip"
                hint="Days. People who got another marketing email lately are left out, so nobody gets too many. 0 sends to everyone."
              >
                <Input
                  id="mkt-skip"
                  type="number"
                  min={0}
                  max={30}
                  disabled={!canEdit || save.isPending}
                  value={values.skip_recent_days}
                  onChange={(e) =>
                    set('skip_recent_days', Math.max(0, Math.min(30, Number(e.target.value) || 0)))
                  }
                />
              </Field>
            )}
            {canEdit && (
              <Button variant="secondary" size="sm" className="w-fit" onClick={() => setDialog('group')}>
                Save as a group
              </Button>
            )}
            <p className="text-xs text-ink-subtle">
              {service
                ? 'Every customer in the group with a confirmed address and an open account, opted in or not. Use it only for essential changes to the service, never to sell.'
                : 'Only people who opted in to product news, with a confirmed address and an open account.'}{' '}
              Internal accounts and addresses that bounced or marked an email as spam are always left out.
            </p>
          </Card>
        </div>
      </div>

      {campaign && campaign.status !== 'draft' && (
        <div className="mt-8">
          <MarketingSends campaignId={campaign.id} />
        </div>
      )}

      {dialog === 'group' && (
        <GroupDialog
          group={null}
          meta={meta}
          initialFilters={cleanFilters(values.filters)}
          onClose={() => setDialog(null)}
        />
      )}
      {campaign && (
        <>
          <SendDialog
            campaign={campaign}
            open={dialog === 'send' && canSend}
            onOpenChange={(open) => setDialog(open ? 'send' : null)}
          />
          <Dialog open={dialog === 'delete'} onOpenChange={(open) => setDialog(open ? 'delete' : null)}>
            <DialogContent
              title={draft ? 'Delete this draft?' : 'Delete this marketing email and its results?'}
              description={
                draft
                  ? 'It hasn’t been sent, so nobody will notice.'
                  : 'Emails already sent can’t be recalled. Its results and the list of who got it are deleted; the audit log keeps who deleted it and when.'
              }
              footer={
                <>
                  <DialogClose asChild>
                    <Button variant="ghost">Cancel</Button>
                  </DialogClose>
                  <Button
                    variant="danger"
                    loading={remove.isPending}
                    onClick={() =>
                      remove.mutate(campaign.id, {
                        onSuccess: () => {
                          toast.success(draft ? 'Draft deleted.' : 'Marketing email deleted.')
                          void navigate('/marketing', { replace: true })
                        },
                        onError: (err) => toast.error(errorMessage(err)),
                      })
                    }
                  >
                    Delete
                  </Button>
                </>
              }
            />
          </Dialog>
        </>
      )}
    </>
  )
}

/**
 * How a sent email did. Bounces and spam complaints always count; opens and clicks only for a
 * marketing email (a service notice says "Not tracked"), with a warning when a marketing email has
 * reached 20 people or more and a day later still has no open or click recorded.
 */
function Results({ results, recipients }: { results: CampaignResults; recipients: number }) {
  const { progress, purchases } = results
  const tracked = results.tracked
  const of = (n: number, whole: number) => `${numberFmt.format(n)} (${percent(n, whole)})`

  return (
    <Card className="mb-6 grid gap-1.5 p-5 text-[13px]" aria-label="Results">
      <b className="text-ink">
        Sent to {numberFmt.format(progress.sent)} of {numberFmt.format(recipients)}
      </b>
      <span className="text-ink-muted">
        {numberFmt.format(progress.queued)} waiting · {numberFmt.format(progress.skipped)} skipped
        (unsubscribed, bounced or closed since) · {numberFmt.format(progress.failed)} failed
      </span>
      <span className="text-ink-muted">
        Bounced {of(results.bounced, progress.sent)} · marked as spam {of(results.complained, progress.sent)}
      </span>
      {results.counts_opens ? (
        tracked > 0 && (
          <span className="text-ink-muted">
            Opened by {of(results.opened ?? 0, tracked)} · clicked by {of(results.clicked ?? 0, tracked)} ·
            unsubscribed {of(results.unsubscribed ?? 0, tracked)}, of {numberFmt.format(tracked)} tracked
            emails. Opens are approximate: some mail apps open every email.
          </span>
        )
      ) : (
        <span className="text-ink-subtle">Opened and clicked: Not tracked. {NOT_TRACKED}</span>
      )}
      {results.tracking_warning && (
        <p
          role="status"
          className="mt-1 flex items-start gap-2 rounded-lg bg-warning-soft px-3 py-2.5 text-ink"
        >
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
          <span>
            No opens or clicks recorded a day after it reached {numberFmt.format(progress.sent)} people. Opens
            may not be reaching Arva SEO: in Amazon SES, check that the configuration set{' '}
            <code>arvaseo-announcements</code> has an event destination for Opens and Clicks that publishes to
            the SNS topic <code>arvaseo-ses-notifications</code>.
          </span>
        </p>
      )}
      <span className="text-ink-muted">
        Purchases within {purchases.days} days: {numberFmt.format(purchases.workspaces)}{' '}
        {purchases.workspaces === 1 ? 'workspace' : 'workspaces'}, {dollars(purchases.revenue_usd)}
        {purchases.after_click_workspaces !== null && purchases.after_click_revenue_usd !== null
          ? ` (after a click: ${numberFmt.format(purchases.after_click_workspaces)}, ${dollars(purchases.after_click_revenue_usd)})`
          : ''}
        . They may have bought anyway.
      </span>
    </Card>
  )
}

function SendDialog({
  campaign,
  open,
  onOpenChange,
}: {
  campaign: Campaign
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const send = useSendCampaign(campaign.id)
  const schedule = useScheduleCampaign(campaign.id)
  const audience = useAudience(campaign.filters, campaign.skip_recent_days, campaign.id, campaign.kind)
  const now = useNow(30_000)
  const [when, setWhen] = useState<'now' | 'later'>(campaign.scheduled_at ? 'later' : 'now')
  const [at, setAt] = useState(() => localInput(campaign.scheduled_at ?? tomorrowAtNine()))
  const count = audience.data?.count ?? 0
  const people = `${numberFmt.format(count)} ${count === 1 ? 'person' : 'people'}`
  const later = when === 'later'
  const atDate = at ? new Date(at) : null
  const validAt = atDate !== null && !Number.isNaN(atDate.getTime()) && atDate.getTime() > now + 2 * 60_000

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={
          audience.isPending
            ? 'Counting who gets it…'
            : later
              ? `Schedule for ${people}?`
              : `Send to ${people} now?`
        }
        description={`“${campaign.subject}” ${
          campaign.kind === 'service'
            ? 'is a service notice: it goes to every customer in its group, opted in or not, each once.'
            : 'goes to the people in its group who opted in to product news, each once, with an unsubscribe link.'
        }${later ? ' It stays a draft until then, and the group is counted again when it goes out.' : ' It can’t be undone.'}`}
        footer={
          <>
            <DialogClose asChild>
              <Button variant="ghost">Cancel</Button>
            </DialogClose>
            <Button
              loading={send.isPending || schedule.isPending}
              disabled={audience.isPending || count === 0 || (later && !validAt)}
              onClick={() =>
                later && atDate
                  ? schedule.mutate(atDate.toISOString(), {
                      onSuccess: (c) => {
                        toast.success(
                          `Scheduled for ${formatDateTime(c.scheduled_at)}. Nothing is sent until then.`,
                        )
                        onOpenChange(false)
                      },
                      onError: (err) => toast.error(errorMessage(err)),
                    })
                  : send.mutate(count, {
                      onSuccess: () => {
                        toast.success(`Sending to ${people}.`)
                        onOpenChange(false)
                      },
                      onError: (err) => {
                        toast.error(errorMessage(err))
                        void audience.refetch()
                      },
                    })
              }
            >
              {later ? 'Schedule' : `Send to ${people}`}
            </Button>
          </>
        }
      >
        <div className="grid gap-3">
          {count === 0 && !audience.isPending && (
            <p role="alert" className="text-sm text-danger">
              Nobody in this group can get it right now. Change the group or how many days to leave out.
            </p>
          )}
          <Segmented<'now' | 'later'>
            label="When"
            value={when}
            onChange={setWhen}
            options={[
              { value: 'now', label: 'Now' },
              { value: 'later', label: 'Later' },
            ]}
          />
          {later && (
            <Field
              label="Send on"
              htmlFor="mkt-send-at"
              required
              hint={`Your time (${Intl.DateTimeFormat().resolvedOptions().timeZone}). At least a few minutes from now, within 90 days.`}
              error={at && !validAt ? 'Choose a time at least a few minutes from now.' : undefined}
            >
              <Input
                id="mkt-send-at"
                type="datetime-local"
                value={at}
                min={localInput(new Date(now + 5 * 60_000).toISOString())}
                onChange={(e) => setAt(e.target.value)}
              />
            </Field>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** An ISO time as a datetime-local value, in the viewer's own time zone. */
function localInput(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function tomorrowAtNine(): string {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  d.setHours(9, 0, 0, 0)
  return d.toISOString()
}
